<template>
    <Card pageTitle="Loyalty Rewards">
        <AllowPermission :permission="PERMISSION_CODES.CMS_VIEW_LOYALTY_REWARD">
            <div class="rewards-wrap">
                <div class="page-head">
                    <h2>Reward Catalog</h2>
                    <p>
                        Configure merchandise, non-stock entitlements, and explicit merchandise discounts. New benefit types start inactive.
                    </p>
                </div>

                <div class="reward-summary">
                    <div>
                        <span class="summary-label">Active rewards</span>
                        <strong>{{ activeRewardCount }}</strong>
                    </div>
                    <div>
                        <span class="summary-label">Inactive rewards</span>
                        <strong>{{ inactiveRewardCount }}</strong>
                    </div>
                    <div>
                        <span class="summary-label">Approval guardrail</span>
                        <strong>Every new SKU needs an approval note</strong>
                    </div>
                </div>

                <div v-if="canCreate" class="create-box">
                    <div class="section-head">
                        <h3>Add reward</h3>
                        <span class="subtle">Search an item, confirm approval, then set the point cost.</span>
                    </div>

                    <label class="field">Reward type
                        <select v-model="form.reward_type" class="native-select" :disabled="saving" @change="changeRewardType">
                            <option value="MERCHANDISE">Merchandise from stock</option>
                            <option value="NON_STOCK">Non-stock entitlement</option>
                            <option value="AMOUNT_OFF">Amount off merchandise</option>
                            <option value="PERCENT_OFF">Percentage off merchandise</option>
                        </select>
                    </label>

                    <div class="reward-builder">
                        <section v-if="form.reward_type === 'MERCHANDISE'" class="builder-section">
                            <h4>1. Item</h4>
                            <div class="grid two">
                                <div class="field">
                                    <label>Warehouse stock view</label>
                                    <select v-model="selectedWarehouseId" class="native-select" @change="searchRewardItems">
                                        <option value="">All warehouses</option>
                                        <option v-for="warehouse in warehouses" :key="warehouse.id" :value="warehouse.id">
                                            {{ warehouse.title || warehouse.name || warehouse.warehouse_name || warehouse.id }}
                                        </option>
                                    </select>
                                </div>
                                <div class="field">
                                    <label>Find item</label>
                                    <CustomInput
                                        v-model="itemSearch"
                                        placeholder="Search by SKU, name, or catalog SKU"
                                        :disabled="saving"
                                        @keyup.native="searchRewardItems"
                                    />
                                </div>
                            </div>

                            <div v-if="rewardItems.length" class="item-results">
                                <button
                                    v-for="item in rewardItems"
                                    :key="item.item_code"
                                    type="button"
                                    class="item-option"
                                    :class="{ selected: selectedItem && selectedItem.item_code === item.item_code }"
                                    @click="selectItem(item)"
                                >
                                    <span class="item-main">
                                        <strong>{{ item.item_name || item.name || item.item_code }}</strong>
                                        <small>{{ item.item_code }} <span v-if="item.catalog_sku">/ {{ item.catalog_sku }}</span></small>
                                    </span>
                                    <span class="item-meta">
                                        <span>Stock: {{ item.stock_qty }}</span>
                                        <small v-if="item.stock_by_warehouse && item.stock_by_warehouse.length">
                                            {{ summarizeWarehouseStock(item.stock_by_warehouse) }}
                                        </small>
                                        <span :class="['status-badge', item.loyalty_reward_eligible ? 'ok' : 'warn']">
                                            {{ item.loyalty_reward_eligible ? "Approved" : "Needs approval" }}
                                        </span>
                                    </span>
                                </button>
                            </div>
                        </section>

                        <section v-if="form.reward_type === 'MERCHANDISE'" class="builder-section selected-section">
                            <h4>2. Approval</h4>
                            <div v-if="selectedItem" class="selected-item">
                                <div class="selected-top">
                                    <div>
                                        <strong>{{ selectedItem.item_name || selectedItem.name || selectedItem.item_code }}</strong>
                                        <div class="muted">{{ selectedItem.item_code }} · Stock: {{ selectedItem.stock_qty }}</div>
                                    </div>
                                    <span :class="['status-badge', selectedItem.loyalty_reward_eligible ? 'ok' : 'warn']">
                                        {{ selectedItem.loyalty_reward_eligible ? "Approved" : "Needs approval" }}
                                    </span>
                                </div>
                                <div v-if="selectedItem.classification_warning" class="warning">
                                    {{ selectedItem.classification_warning }}
                                </div>
                                <div v-if="selectedItem.loyalty_reward_approval_note" class="approval-note">
                                    Approval note: {{ selectedItem.loyalty_reward_approval_note }}
                                </div>
                                <div v-if="selectedItem.stock_by_warehouse && selectedItem.stock_by_warehouse.length" class="stock-list">
                                    <strong>Warehouse stock</strong>
                                    <div v-for="stock in selectedItem.stock_by_warehouse" :key="stock.warehouse_id">
                                        {{ warehouseName(stock.warehouse_id) }}: {{ stock.stock_qty }}
                                    </div>
                                </div>
                            </div>
                            <p v-else class="empty compact">Select an item to review approval and stock.</p>
                        </section>

                        <section class="builder-section config-section">
                            <h4>3. Reward</h4>
                            <div class="grid">
                                <div class="field">
                                    <label>Display name</label>
                                    <CustomInput v-model="form.name" :disabled="saving" />
                                </div>
                                <div class="field">
                                    <label>Points cost</label>
                                    <CustomInput type="number" :step="1" v-model="form.points_cost" :disabled="saving" />
                                </div>
                                <div class="field">
                                    <label>Min tier (optional)</label>
                                    <CustomInput v-model="form.min_tier" :disabled="saving" />
                                </div>
                            </div>
                            <div class="field">
                                <label>{{ form.reward_type === 'MERCHANDISE' ? 'Approval note (required for newly approved SKUs)' : 'Approval note for future activation' }}</label>
                                <CustomInput v-model="form.approval_note" :disabled="saving || (form.reward_type === 'MERCHANDISE' && selectedItem && selectedItem.loyalty_reward_eligible)" />
                            </div>
                            <BenefitPolicyFields v-if="form.reward_type !== 'MERCHANDISE'" v-model="form.benefit_policy" :kind="form.reward_type" :disabled="saving" />
                            <p v-if="form.reward_type !== 'MERCHANDISE'" class="muted">No stock item is created or deducted. Enter the actual approved points cost and benefit terms; this draft starts inactive.</p>
                            <Button @click="createReward" :disabled="saving">{{ saving ? "Saving..." : "Add reward" }}</Button>
                        </section>
                    </div>
                </div>

                <h3 class="list-title">Configured rewards</h3>
                <div class="table-shell" v-if="rewards.length">
                    <table class="rewards-table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Item code</th>
                                <th>Points</th>
                                <th>Min tier</th>
                                <th>Stock</th>
                                <th>Approval</th>
                                <th>Status</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="reward in rewards" :key="reward.id">
                                <td>
                                    <CustomInput
                                        v-if="canEdit"
                                        v-model="reward.name"
                                        :disabled="reward.busy"
                                        @blur.native="isMerchandise(reward) && saveReward(reward)"
                                    />
                                    <div v-else>{{ reward.name }}</div>
                                    <small>{{ reward.reward_type || "MERCHANDISE" }}</small>
                                    <p v-if="reward.approvalConflict" class="state-warn" role="alert">{{ reward.approvalConflict }}</p>
                                    <Button v-if="canEdit && (reward.approvalConflict || (!isMerchandise(reward) && !reward.approval_terms_hash))" borderOnly @click="refreshRewardTerms(reward)" :disabled="reward.busy">Reload saved terms (discard local edits)</Button>
                                    <small v-if="reward.approval_warning" class="state-warn">{{ reward.approval_warning }}</small>
                                </td>
                                <td>{{ reward.item_code || "No stock item" }}</td>
                                <td>
                                    <CustomInput
                                        v-if="canEdit"
                                        type="number"
                                        :step="1"
                                        v-model="reward.points_cost"
                                        :disabled="reward.busy"
                                        @blur.native="isMerchandise(reward) && saveReward(reward)"
                                    />
                                    <span v-else>{{ reward.points_cost }}</span>
                                </td>
                                <td>
                                    <select
                                        v-if="canEdit"
                                        v-model="reward.min_tier"
                                        class="native-select compact"
                                        :disabled="reward.busy"
                                        @change="isMerchandise(reward) && saveReward(reward)"
                                    >
                                        <option value="">No minimum</option>
                                        <option v-for="tier in tiers" :key="tier.id" :value="tier.name">
                                            {{ tier.name }}
                                        </option>
                                    </select>
                                    <span v-else>{{ reward.min_tier || "-" }}</span>
                                </td>
                                <td>
                                    <Button v-if="isMerchandise(reward)" borderOnly @click="inspectRewardStock(reward)" :disabled="loadingStockFor === reward.id">
                                        {{ loadingStockFor === reward.id ? "Loading..." : "View stock" }}
                                    </Button>
                                    <span v-else>Stock not used</span>
                                    <div v-if="reward.stockDetails" class="mini-stock">
                                        <div v-for="stock in reward.stockDetails" :key="stock.warehouse_id">
                                            {{ warehouseName(stock.warehouse_id) }}: {{ stock.stock_qty }}
                                        </div>
                                    </div>
                                </td>
                                <td>
                                    <div>{{ reward.approval_note || "-" }}</div>
                                    <button
                                        v-if="reward.approval_history && reward.approval_history.length"
                                        type="button"
                                        class="text-link"
                                        @click="toggleHistory(reward)"
                                    >
                                        {{ reward.showHistory ? "Hide" : "Show" }} history
                                    </button>
                                    <div v-if="reward.showHistory" class="history-list">
                                        <div v-for="entry in reward.approval_history" :key="`${reward.id}-${entry.at}-${entry.action}`">
                                            <strong>{{ entry.action }}</strong>
                                            <span>{{ formatDate(entry.at) }}</span>
                                            <small>{{ entry.user_name || entry.user_id || "System" }}</small>
                                            <p v-if="entry.note">{{ entry.note }}</p>
                                        </div>
                                    </div>
                                </td>
                                <td>
                                    <span :class="['status-badge', reward.active ? 'ok' : 'neutral']">
                                        {{ reward.active ? "Active" : "Inactive" }}
                                    </span>
                                </td>
                                <td>
                                    <Button v-if="canEdit" borderOnly @click="toggleActive(reward)" :disabled="reward.busy">
                                        {{ reward.active ? "Deactivate" : "Activate" }}
                                    </Button>
                                    <label v-if="canEdit && !isMerchandise(reward) && !reward.active">Activation approval note<input v-model.trim="reward.activationNote" class="native-select" /></label>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <p v-else class="empty">No rewards configured yet.</p>

                <section v-for="reward in typedRewards" :key="`terms-${reward.id}`" class="create-box">
                    <h3>{{ reward.name }} — {{ reward.reward_type }} terms</h3>
                    <BenefitPolicyFields v-model="reward.policyEdit" :kind="reward.reward_type" :disabled="!canEdit || reward.busy" />
                    <p class="muted">Name, points cost, and minimum tier above are saved together with these benefit terms. Saving leaves this reward inactive until approved again.</p>
                    <Button v-if="canEdit" @click="saveTypedRewardTerms(reward)" :disabled="reward.busy">Save reward terms as inactive</Button>
                </section>

                <section v-if="canViewBenefits" class="create-box">
                    <h3>Customer benefits and fulfillment</h3>
                    <label class="field">Customer ID<input v-model.trim="benefitCustomerId" class="native-select" @input="clearBenefits" /></label>
                    <Button borderOnly @click="loadBenefits" :disabled="benefitsLoading || !benefitCustomerId">{{ benefitsLoading ? "Loading…" : "Load customer benefits" }}</Button>
                    <p v-if="benefitCustomer"><strong>{{ benefitCustomer.customer_name || "Unnamed customer" }}</strong> · Customer ID: {{ benefitCustomer.id }}</p>
                    <article v-for="benefit in benefits" :key="benefit.id" class="benefit-row">
                        <h4>{{ benefit.name }} · {{ benefit.kind }} · {{ benefit.status }}</h4>
                        <p>ID: {{ benefit.id }} · Expires: {{ formatDate(benefit.expires_at) }} · Points spent: {{ benefit.points_spent || 0 }}</p>
                        <div v-if="canManageBenefits && benefitUsable(benefit)">
                            <label class="field">Action reason<input v-model.trim="benefit.actionReason" class="native-select" :disabled="benefit.busy" /></label>
                            <Button v-if="benefit.kind === 'NON_STOCK'" @click="actOnBenefit(benefit, 'fulfill')" :disabled="benefit.busy">Record fulfillment</Button>
                            <Button borderOnly @click="actOnBenefit(benefit, 'void')" :disabled="benefit.busy">Void unconsumed benefit</Button>
                        </div>
                        <details><summary>Audit history</summary><p v-for="(entry, index) in benefit.audit_history || []" :key="index">{{ entry.action }} · {{ formatDate(entry.at) }} · {{ entry.user_name || entry.user_id || 'System' }} · {{ entry.note || entry.reason || '' }}</p></details>
                    </article>
                    <p v-if="benefitCustomer && !benefits.length && !benefitsLoading" class="empty">No benefits for this customer.</p>
                </section>
            </div>
        </AllowPermission>
    </Card>
</template>

<script>
import { mapState } from "vuex"
import { checkHasPermission, PERMISSION_CODES } from "../../store/permission"
import { http } from "../../utils/http"
import Card from "../../components/UI/Card.vue"
import Button from "../../components/UI/Button.vue"
import CustomInput from "../../components/crud/custom-input.vue"
import AllowPermission from "../../components/layout/AllowPermission.vue"
import BenefitPolicyFields from "../../components/loyalty/BenefitPolicyFields.vue"
import LoyaltyFetchApi from "../../utils/fetch/loyalty-fetch-api"
import { errMsg, confirmDialog } from "../../utils/common"
import { benefitPolicyEditor, newBenefitPolicyEditor, benefitPolicyPayload, validateBenefitPolicy } from "../../utils/loyalty-benefit-policy"
import { formatBusinessInstant } from "../../utils/business-date"

export default {
    components: { Card, Button, CustomInput, AllowPermission, BenefitPolicyFields },
    data() {
        return {
            PERMISSION_CODES,
            rewards: [],
            tiers: [],
            warehouses: [],
            rewardItems: [],
            selectedWarehouseId: "",
            selectedItem: null,
            itemSearch: "",
            saving: false,
            loadingStockFor: "",
            form: this.emptyRewardForm(),
            benefitCustomerId: "", benefitCustomer: null, benefits: [], benefitsLoading: false, benefitsRequest: 0,
        }
    },
    computed: {
        ...mapState("auth", ["user"]),
        canCreate() {
            return checkHasPermission({
                isAdmin: this.user?.isAdmin,
                permissionId: PERMISSION_CODES.CMS_CREATE_LOYALTY_REWARD,
                userPermissionIds: this.user?.permissionIds || [],
            })
        },
        canEdit() {
            return checkHasPermission({
                isAdmin: this.user?.isAdmin,
                permissionId: PERMISSION_CODES.CMS_EDIT_LOYALTY_REWARD,
                userPermissionIds: this.user?.permissionIds || [],
            })
        },
        activeRewardCount() {
            return this.rewards.filter((reward) => reward.active).length
        },
        inactiveRewardCount() {
            return this.rewards.filter((reward) => !reward.active).length
        },
        typedRewards() { return this.rewards.filter((reward) => !this.isMerchandise(reward)) },
        canManageBenefits() { return checkHasPermission({ isAdmin: this.user?.isAdmin, permissionId: PERMISSION_CODES.CMS_EDIT_LOYALTY, userPermissionIds: this.user?.permissionIds || [] }) },
        canViewBenefits() { return checkHasPermission({ isAdmin: this.user?.isAdmin, permissionId: PERMISSION_CODES.CMS_VIEW_LOYALTY, userPermissionIds: this.user?.permissionIds || [] }) },
    },
    async mounted() {
        await Promise.all([this.loadRewards(), this.loadTiers(), this.loadWarehouses()])
        await this.searchRewardItems()
    },
    beforeDestroy() { this.benefitsRequest += 1 },
    methods: {
        emptyRewardForm() {
            return { name: "", points_cost: "", min_tier: "", approval_note: "", reward_type: "MERCHANDISE", benefit_policy: benefitPolicyEditor() }
        },
        changeRewardType() {
            this.selectedItem = null
            this.form.benefit_policy = newBenefitPolicyEditor(this.form.reward_type === "MERCHANDISE" ? "" : this.form.reward_type)
        },
        isMerchandise(reward) { return !reward.reward_type || reward.reward_type === "MERCHANDISE" },
        rewardTerms(reward, policyEdit = reward.policyEdit) {
            return JSON.stringify({ reward_type: reward.reward_type, name: String(reward.name || "").trim(),
                points_cost: Number(reward.points_cost), min_tier: reward.min_tier || null,
                benefit_policy: benefitPolicyPayload(policyEdit || benefitPolicyEditor(reward.benefit_policy || {}, reward.reward_type)) })
        },
        rewardEditorState(reward) {
            const policyEdit = benefitPolicyEditor(reward.benefit_policy || {}, reward.reward_type)
            return { policyEdit, viewedTerms: this.rewardTerms(reward, policyEdit), activationNote: "", busy: false, approvalConflict: "" }
        },
        typedRequestTerms(reward) {
            return this.isMerchandise(reward) ? {} : { approval_terms_hash: reward.approval_terms_hash }
        },
        applyRewardResponse(reward, response, resetPolicy = false) {
            if (!response) return
            const savedState = this.rewardEditorState(response)
            Object.assign(reward, response, { viewedTerms: savedState.viewedTerms, approvalConflict: "" })
            if (resetPolicy) reward.policyEdit = savedState.policyEdit
        },
        rewardConflict(reward, err) {
            const code = err?.response?.data?.code
            if (!["REWARD_APPROVAL_TERMS_CHANGED", "REWARD_TERMS_CHANGED"].includes(code)) return false
            reward.approvalConflict = "Saved reward terms changed. Your local edits are preserved. Reload and review the saved terms before approval."
            this.$toast.error(reward.approvalConflict)
            return true
        },
        async refreshRewardTerms(reward) {
            if (!this.canEdit || reward.busy) return
            reward.busy = true
            try {
                const confirmed = await confirmDialog({ title: "Reload saved reward terms", text: `Discard local edits for ${reward.name} and reload its current saved terms for review?`, confirmButtonText: "Discard edits and reload" })
                if (!confirmed || !this.canEdit) return
                const response = ((await LoyaltyFetchApi.fetchRewards()) || []).find((row) => row.id === reward.id)
                if (!response) { this.$toast.error("This reward is no longer available"); return }
                this.applyRewardResponse(reward, response, true)
                reward.activationNote = ""
            } catch (err) { this.$toast.error(errMsg(err, "Could not reload saved reward terms")) }
            finally { reward.busy = false }
        },
        benefitUsable(benefit) { return benefit.status === "ISSUED" && (!benefit.expires_at || new Date(benefit.expires_at).getTime() > Date.now()) },
        async saveTypedRewardTerms(reward) {
            if (!this.canEdit || reward.busy) return
            if (!reward.approval_terms_hash) { this.$toast.error("Reload and review saved reward terms before editing"); return }
            const name = String(reward.name || "").trim()
            if (!name) { this.$toast.error("Reward name is required"); return }
            const pointsCost = Number(reward.points_cost)
            if (!Number.isSafeInteger(pointsCost) || pointsCost <= 0) { this.$toast.error("Points cost must be a positive whole number"); return }
            const policy = benefitPolicyPayload(reward.policyEdit)
            const error = validateBenefitPolicy(policy)
            if (error) { this.$toast.error(error); return }
            reward.busy = true
            try {
                const response = await LoyaltyFetchApi.updateReward(reward.id, { name, points_cost: pointsCost, min_tier: reward.min_tier || null,
                    benefit_policy: policy, active: false, ...this.typedRequestTerms(reward) })
                this.applyRewardResponse(reward, response, true)
                this.$toast.success("Reward terms saved; reward is inactive")
            } catch (err) { if (!this.rewardConflict(reward, err)) this.$toast.error(errMsg(err, "Could not save reward terms")) }
            finally { reward.busy = false }
        },
        clearBenefits() {
            this.benefitsRequest += 1
            this.benefitCustomer = null
            this.benefits = []
            this.benefitsLoading = false
        },
        async loadBenefits() {
            if (!this.canViewBenefits) return
            const customerId = this.benefitCustomerId.trim()
            if (!customerId) return
            const request = ++this.benefitsRequest
            this.benefitCustomer = null
            this.benefits = []
            this.benefitsLoading = true
            try {
                const [account, benefits] = await Promise.all([
                    LoyaltyFetchApi.fetchCustomerLoyalty(customerId), LoyaltyFetchApi.fetchCustomerBenefits(customerId),
                ])
                if (request !== this.benefitsRequest || customerId !== this.benefitCustomerId.trim()) return
                if (String(account?.customer?.id) !== customerId) throw new Error("Customer identity unavailable")
                this.benefitCustomer = account.customer
                this.benefits = (benefits || []).filter((benefit) => String(benefit.customer_id) === customerId).map((benefit) => ({ ...benefit, actionReason: "", busy: false }))
            } catch (err) {
                if (request === this.benefitsRequest) this.$toast.error(errMsg(err, "Could not load customer benefits"))
            } finally {
                if (request === this.benefitsRequest) this.benefitsLoading = false
            }
        },
        async actOnBenefit(benefit, action) {
            if (!this.canManageBenefits || benefit.busy || !this.benefitUsable(benefit) || !this.benefitCustomer) return
            const customerId = String(this.benefitCustomer.id)
            const request = this.benefitsRequest
            if (customerId !== this.benefitCustomerId.trim() || String(benefit.customer_id) !== customerId) return
            if (!["fulfill", "void"].includes(action) || (action === "fulfill" && benefit.kind !== "NON_STOCK")) return
            const reason = String(benefit.actionReason || "").trim()
            if (!reason) { this.$toast.error("An action reason is required"); return }
            const customerName = this.benefitCustomer.customer_name || "Unnamed customer"
            benefit.busy = true
            try {
                const confirmed = await confirmDialog({ title: action === "fulfill" ? "Record fulfillment" : "Void benefit", text: `${customerName} (Customer ID: ${customerId}): ${action} ${benefit.name}, benefit ${benefit.id}? ${action === "void" ? "Original point allocations are restored only when the server permits it." : "This records staff fulfillment without a stock deduction."}`, confirmButtonText: action === "fulfill" ? "Record fulfillment" : "Void benefit" })
                if (!confirmed || !this.canManageBenefits || request !== this.benefitsRequest || customerId !== this.benefitCustomerId.trim()) return
                if (action === "fulfill") await LoyaltyFetchApi.fulfillBenefit(benefit.id, reason)
                else await LoyaltyFetchApi.voidBenefit(benefit.id, reason)
                if (request !== this.benefitsRequest || customerId !== this.benefitCustomerId.trim()) return
                this.$toast.success(action === "fulfill" ? "Fulfillment recorded" : "Benefit voided")
                await this.loadBenefits()
            } catch (err) {
                if (request === this.benefitsRequest) this.$toast.error(errMsg(err, "Could not update benefit"))
            } finally { benefit.busy = false }
        },
        async loadRewards() {
            try {
                this.rewards = ((await LoyaltyFetchApi.fetchRewards()) || []).map((reward) => ({
                    ...reward,
                    showHistory: false,
                    stockDetails: null,
                    ...this.rewardEditorState(reward),
                }))
            } catch (err) {
                this.$toast.error(errMsg(err, "Could not load rewards"))
            }
        },
        async loadTiers() {
            try {
                this.tiers = ((await LoyaltyFetchApi.fetchTiers()) || []).filter((tier) => tier.active)
            } catch (err) {
                this.tiers = []
            }
        },
        async loadWarehouses() {
            try {
                const res = await http.get("/warehouses")
                this.warehouses = res.data?.result || []
            } catch (err) {
                this.warehouses = []
            }
        },
        async searchRewardItems() {
            try {
                this.rewardItems = (await LoyaltyFetchApi.fetchRewardItems(this.itemSearch, this.selectedWarehouseId)) || []
            } catch (err) {
                this.rewardItems = []
            }
        },
        selectItem(item) {
            this.selectedItem = item
            this.form.name = item.item_name || item.name || item.item_code
            if (item.loyalty_reward_eligible) this.form.approval_note = ""
        },
        warehouseName(id) {
            const warehouse = this.warehouses.find((entry) => String(entry.id) === String(id))
            return warehouse?.title || warehouse?.name || warehouse?.warehouse_name || id || "Unknown"
        },
        summarizeWarehouseStock(stockRows = []) {
            const visible = stockRows.slice(0, 2).map((row) => `${this.warehouseName(row.warehouse_id)} ${row.stock_qty}`)
            const extra = stockRows.length > 2 ? ` +${stockRows.length - 2} more` : ""
            return `${visible.join(", ")}${extra}`
        },
        formatDate(value) {
            if (!value) return "-"
            return formatBusinessInstant(value)
        },
        toggleHistory(reward) {
            reward.showHistory = !reward.showHistory
        },
        async inspectRewardStock(reward) {
            this.loadingStockFor = reward.id
            try {
                const items = await LoyaltyFetchApi.fetchRewardItems(reward.item_code, "")
                const match = (items || []).find((item) => item.item_code === reward.item_code)
                this.$set(reward, "stockDetails", match?.stock_by_warehouse || [])
            } catch (err) {
                this.$toast.error(errMsg(err, "Could not load reward stock"))
            }
            this.loadingStockFor = ""
        },
        validateRewardForm() {
            const merchandise = this.form.reward_type === "MERCHANDISE"
            if (merchandise && !this.selectedItem) return "Select an item first"
            if (!this.form.name || !this.form.name.trim()) return "Display name is required"
            const pointsCost = Math.round(Number(this.form.points_cost) || 0)
            if (pointsCost <= 0) return "Points cost must be greater than 0"
            if (!merchandise && (!Number.isSafeInteger(Number(this.form.points_cost)) || Number(this.form.points_cost) <= 0)) return "Points cost must be a positive whole number"
            if (!merchandise) return validateBenefitPolicy(benefitPolicyPayload(this.form.benefit_policy))
            if (!this.selectedItem.loyalty_reward_eligible && !String(this.form.approval_note || "").trim()) {
                return "Approval note is required for this SKU"
            }
            return ""
        },
        async createReward() {
            if (!this.canCreate || this.saving) return
            const error = this.validateRewardForm()
            if (error) {
                this.$toast.error(error)
                return
            }
            this.saving = true
            try {
                await LoyaltyFetchApi.createReward({
                    reward_type: this.form.reward_type,
                    ...(this.form.reward_type === "MERCHANDISE" ? { item_code: this.selectedItem.item_code } : {
                        active: false, benefit_policy: benefitPolicyPayload(this.form.benefit_policy),
                    }),
                    name: this.form.name.trim(),
                    points_cost: Math.round(Number(this.form.points_cost) || 0),
                    min_tier: this.form.min_tier?.trim() || null,
                    approval_note: this.form.approval_note?.trim() || "",
                })
                this.$toast.success("Reward added")
                this.selectedItem = null
                this.form = this.emptyRewardForm()
                await Promise.all([this.loadRewards(), this.searchRewardItems()])
            } catch (err) {
                this.$toast.error(errMsg(err, "Could not add reward"))
            }
            this.saving = false
        },
        async saveReward(reward) {
            if (!this.canEdit || reward.busy) return
            if (!this.isMerchandise(reward)) return this.saveTypedRewardTerms(reward)
            if (!this.isMerchandise(reward) && !reward.approval_terms_hash) { this.$toast.error("Reload and review saved reward terms before editing"); return }
            const name = String(reward.name || "").trim()
            if (!name) {
                this.$toast.error("Reward name is required")
                return
            }
            const pointsCost = Math.round(Number(reward.points_cost) || 0)
            if (!this.isMerchandise(reward) && !Number.isSafeInteger(Number(reward.points_cost))) { this.$toast.error("Points cost must be a positive whole number"); return }
            if (pointsCost <= 0) {
                this.$toast.error("Points cost must be greater than 0")
                return
            }
            reward.busy = true
            try {
                const response = await LoyaltyFetchApi.updateReward(reward.id, {
                    name,
                    points_cost: pointsCost,
                    min_tier: reward.min_tier || null,
                    active: this.isMerchandise(reward) ? !!reward.active : false,
                    ...this.typedRequestTerms(reward),
                })
                this.applyRewardResponse(reward, response)
                this.$toast.success("Reward updated")
            } catch (err) {
                if (!this.rewardConflict(reward, err)) this.$toast.error(errMsg(err, "Could not update reward"))
            }
            finally { reward.busy = false }
        },
        async toggleActive(reward) {
            if (!this.canEdit || reward.busy) return
            const nextActive = !reward.active
            if (nextActive && !this.isMerchandise(reward) && this.rewardTerms(reward) !== reward.viewedTerms) { this.$toast.error("Save changed reward terms before activation"); return }
            if (nextActive && !this.isMerchandise(reward) && (!reward.approval_terms_hash || reward.approvalConflict)) { this.$toast.error("Reload and review saved reward terms before activation"); return }
            if (nextActive && !this.isMerchandise(reward) && !reward.activationNote.trim()) { this.$toast.error("An activation approval note is required"); return }
            const approvalTermsHash = reward.approval_terms_hash
            reward.busy = true
            try {
                if (nextActive && !await confirmDialog({ title: "Activate reward", text: `Activate ${reward.name} at ${reward.points_cost} points with its saved terms?`, confirmButtonText: "Activate reward" })) return
                if (!this.canEdit) return
                if (nextActive && !this.isMerchandise(reward) && (this.rewardTerms(reward) !== reward.viewedTerms || reward.approval_terms_hash !== approvalTermsHash)) { this.$toast.error("Reward terms changed while confirming. Review the saved terms again."); return }
                if (nextActive) {
                    const response = await LoyaltyFetchApi.updateReward(reward.id, { active: true, ...this.typedRequestTerms(reward), ...(!this.isMerchandise(reward) ? { approval_note: reward.activationNote.trim() } : {}) })
                    this.applyRewardResponse(reward, response, true)
                } else {
                    await LoyaltyFetchApi.deactivateReward(reward.id)
                    reward.active = false
                }
                this.$toast.success(nextActive ? "Reward activated" : "Reward deactivated")
            } catch (err) {
                if (!this.rewardConflict(reward, err)) this.$toast.error(errMsg(err, "Could not update reward"))
            }
            finally { reward.busy = false }
        },
    },
}
</script>

<style lang="scss" scoped>
.rewards-wrap { max-width: 1120px; }
.page-head { margin-bottom: 20px; }
.page-head h2 { margin: 0 0 6px; font-size: 24px; font-weight: 700; color: #0b302c; }
.page-head p { margin: 0; color: #5f6368; line-height: 1.45; }
.reward-summary { display: grid; grid-template-columns: 140px 150px 1fr; gap: 12px; margin-bottom: 18px; }
.reward-summary > div { border: 1px solid #e0e0e0; border-radius: 8px; padding: 12px; background: #fff; }
.reward-summary strong { display: block; margin-top: 4px; color: #0b302c; }
.summary-label { color: #80868b; font-size: 12px; font-weight: 700; text-transform: uppercase; }
.create-box { border: 1px solid #e0e0e0; border-radius: 8px; padding: 16px; margin-bottom: 24px; }
.benefit-row { border-top: 1px solid #d5e4e1; padding: 14px 0; overflow-wrap: anywhere; }
.section-head { display: flex; justify-content: space-between; gap: 16px; align-items: baseline; margin-bottom: 14px; }
.section-head h3, .list-title { font-weight: 700; margin-bottom: 12px; color: #111827; }
.subtle { color: #6b7280; font-size: 13px; }
.reward-builder { display: grid; gap: 14px; }
.builder-section { border-top: 1px solid #edf0f2; padding-top: 14px; }
.builder-section h4 { font-size: 14px; font-weight: 700; margin-bottom: 10px; color: #0b302c; }
.grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; margin-bottom: 12px; }
.grid.two { grid-template-columns: 1fr 2fr; }
.field { margin-bottom: 12px; }
.field label { display: block; font-weight: 600; margin-bottom: 6px; }
.native-select { width: 100%; min-height: 40px; border: 1px solid #d9dee3; border-radius: 6px; padding: 8px; background: #fff; }
.native-select.compact { min-width: 130px; }
.item-results { display: grid; gap: 8px; margin-bottom: 12px; max-height: 240px; overflow: auto; }
.item-option { display: flex; justify-content: space-between; gap: 12px; width: 100%; text-align: left; border: 1px solid #e3e6ea; border-radius: 8px; padding: 10px 12px; background: #fff; }
.item-option.selected { border-color: #0b3f3a; background: #f1f7f6; }
.item-main, .item-meta { display: flex; flex-direction: column; gap: 3px; }
.item-meta { align-items: flex-end; }
.selected-item { border: 1px solid #d5e4e1; background: #f7fbfa; border-radius: 8px; padding: 12px; margin-bottom: 12px; }
.selected-top { display: flex; justify-content: space-between; gap: 12px; align-items: flex-start; margin-bottom: 8px; }
.muted, small { color: #6b7280; }
.warning, .state-warn { color: #9a5b00; }
.approval-note, .state-ok { color: #287a3e; }
.stock-list { margin-top: 10px; border-top: 1px solid #d5e4e1; padding-top: 10px; color: #374151; }
.stock-list strong { display: block; margin-bottom: 4px; color: #0b302c; }
.mini-stock { margin-top: 8px; color: #374151; font-size: 12px; line-height: 1.45; }
.text-link { border: 0; background: transparent; color: #0b3f3a; padding: 0; font-weight: 700; cursor: pointer; }
.history-list { margin-top: 8px; display: grid; gap: 8px; min-width: 220px; }
.history-list > div { border-left: 3px solid #d5e4e1; padding-left: 8px; color: #374151; }
.history-list strong, .history-list span, .history-list small { display: block; }
.history-list p { margin: 4px 0 0; color: #4b5563; }
.status-badge { display: inline-flex; align-items: center; border-radius: 999px; padding: 3px 8px; font-size: 12px; font-weight: 700; white-space: nowrap; }
.status-badge.ok { background: #e8f5ec; color: #1f7a3b; }
.status-badge.warn { background: #fff3d6; color: #9a5b00; }
.status-badge.neutral { background: #eef0f2; color: #4b5563; }
.table-shell { max-width: 100%; overflow-x: auto; border: 1px solid #edf0f2; border-radius: 8px; }
.rewards-table { width: 100%; border-collapse: collapse; min-width: 1080px; table-layout: fixed; }
.rewards-table th, .rewards-table td { text-align: left; padding: 8px; border-bottom: 1px solid #eee; vertical-align: top; }
.rewards-table th { white-space: nowrap; background: #fafafa; }
.rewards-table th:nth-child(1), .rewards-table td:nth-child(1) { width: 17%; }
.rewards-table th:nth-child(2), .rewards-table td:nth-child(2) { width: 13%; overflow-wrap: anywhere; }
.rewards-table th:nth-child(3), .rewards-table td:nth-child(3) { width: 9%; }
.rewards-table th:nth-child(4), .rewards-table td:nth-child(4) { width: 13%; }
.rewards-table th:nth-child(5), .rewards-table td:nth-child(5) { width: 13%; }
.rewards-table th:nth-child(6), .rewards-table td:nth-child(6) { width: 18%; }
.rewards-table th:nth-child(7), .rewards-table td:nth-child(7) { width: 8%; }
.rewards-table th:nth-child(8), .rewards-table td:nth-child(8) { width: 9%; min-width: 112px; }
.rewards-table tr:last-child td { border-bottom: 0; }
.empty { color: #80868b; }
.empty.compact { margin: 0; }
@media (max-width: 900px) {
    .reward-summary { grid-template-columns: 1fr; }
    .grid, .grid.two { grid-template-columns: 1fr; }
    .item-option { flex-direction: column; }
    .item-meta { align-items: flex-start; }
    .section-head { flex-direction: column; }
}
</style>
